import React, { useEffect } from "react";
import { observer } from "mobx-react";
import { NavigateFunction } from "react-router-dom";
import styled from "styled-components"; 
import PaymentMethodDescripton from "./PaymentMethodDescripton"; 
import { ApplicationStatus, NewPaymentRelatedSource, PaymentMethodPriority } from "../enums";
import { ContactReferenceModel, MerchantAccount, PurchasePaymentIntentModel, UpsertPaymentMethodModel, UpsertPaymentMethodSummaryModel } from "../models";
import { CustomLabel, FlexContainer, IAPICommonResponse, IAPIResponse, Input, Loader, ModalComponent, SapphireBlueButton, ToastService, withRouter } from "@mindmemobile/mm-shared-global";
import { AuthService, CheckOutService, ContactService, MerchantAccountService, PaymentMethodService } from "../services";
import { IAPICreatePaymentMethodModel } from "../interfaces";
import RenderContactInfo from "./RenderContactInfo";
import MerchantAccountsDropDown from "./MerchantAccountsDropDown";
import PaymentForm from "./PaymentForm";

type IProps = {
  navigate: NavigateFunction;
  onClose: (refresh: boolean) => void;
  source: keyof typeof NewPaymentRelatedSource;
  sourceId: string; //id of contact, subscription, invoice, checkout, purchaselinks
  paymentMethodData: UpsertPaymentMethodModel;
};

/*
RULES
General Rules
1. Only One Primary Method:
• There can only be one primary payment method at any given time.
2. Only One Backup Method:
• There can only be one backup payment method at any given time.
3. No Duplicates:
• A payment method cannot exist as both primary and backup simultaneously.
4. “None” as a State:
• Payment methods not designated as primary or backup are categorized as “none.”

Addition Rules
5. Adding a New Payment Method:
• The user can optionally assign it as primary or backup, following the rules below.

Primary Assignment Rules
6. Assigning a Payment Method as Primary:
• If a payment method is moved to primary, the existing primary method:
• Moves to backup (if there’s no current backup).
• Moves to none (if a backup already exists).

Backup Assignment Rules
7. Assigning a Payment Method as Backup:
• If a payment method is moved to backup, the existing backup method:
• Moves to none.

Switching Between Primary and Backup
8. Moving Backup to Primary:
• If a payment method designated as backup is moved to primary, the existing primary:
• Moves to backup.

Deletion Rules
9. Deleting a Primary Method:
• Not Allowed. A primary method must be reassigned (e.g., set as backup or “none”) before it can be deleted.
10. Deleting a Backup Method:
• The backup method can be deleted, and the system will have no backup until another method is assigned.
11. Deleting a “None” Method:
• Allowed. No restrictions.

Edge Case Rules
12. No Primary or Backup Exists:
• If no primary or backup payment methods exist, the user must assign a method to primary before performing specific actions that require a default payment method.
13. Attempt to Remove All Payment Methods:
• Not Allowed. At least one payment method must always remain in the system unless explicitly allowed (e.g., user is closing their account).
14. Swapping Between Primary and Backup:
• A direct swap between primary and backup is allowed:
• The primary becomes backup.
• The backup becomes primary.

*/
export const PaymentMethodPopupWrapper = styled.div`
  > div {
      display: flex;
      /* align-items: center; */
      margin-bottom: 25px;
      > ${CustomLabel},>div {
          &:first-child {
              margin-right: 10px;
              min-width: 150px;
              width: 22%;
              ${CustomLabel}:not(.semi-header) {
                  font-size: 16px;
                  font-weight: 400;
                  &:not(.standard-lh) {
                      line-height: 40px;
                  }
              }
              ${CustomLabel}.semi-header {
                  font-size: 17px;
                  font-weight: 700;
                  line-height: 20px;
                  margin: 5px 0px 35px;
              }
          }
          &:last-child {
              flex: 1 1 0;
          }
      }
      .item-icon {
          img {
              height: 20px;
              width: 20px;
          }
      }
  }
  &.subscription-popup {
      .payment-form {
          width: 100%;
          max-width: 485px;
      }
  }
  .merchant-account-lh label {
      line-height: 38px;
  }
`;

const UpsertPaymentMethodPopup: React.FC<IProps> = ({ onClose, source, sourceId, paymentMethodData }) => {
  const [data, setPaymentMethodData] = React.useState<UpsertPaymentMethodModel>(
      new UpsertPaymentMethodModel({ ...paymentMethodData, showLoader: true }),
  );
  const [editCardBackUp, setEditCardBackUp] = React.useState<UpsertPaymentMethodModel>(
      new UpsertPaymentMethodModel(),
  );
  const [paymentMethodSummaryInProgress, setPaymentMethodSummaryInProgress] = React.useState(true);
  const [summaryData, setSummaryData] = React.useState<UpsertPaymentMethodSummaryModel>(
      new UpsertPaymentMethodSummaryModel({ showLoader: true }),
  );
  const [isAPIRequestPending, setAPIRequestPending] = React.useState(false);
  const [paymentIntent, setPaymentIntent] = React.useState<PurchasePaymentIntentModel | null>(null);
  const [merchantAccounts, setMerchantAccounts] = React.useState<MerchantAccount[]>([]);
  const [loadPaymentMethodInProgress, setLoadPaymentMethodInProgress] = React.useState(false);

  function createPaymentIntent() {
      CheckOutService.createPaymentIntent(
          source || 'None',
          AuthService.authData.accountId,
          sourceId,
          data.merchantAccount?.createAPIModel2(),
      ).subscribe({
          next: (result) => {
              if (!result.result) {
                  ToastService.showError('Error in creating payment intent');
              } else {
                  const paymentIntent = PurchasePaymentIntentModel.deserializeAPIModel(result.result);
                  setPaymentIntent(paymentIntent);
              }
          },
          error: () => {
              ToastService.showError('Error in creating payment intent');
          },
      });
  }

  function isUpdate() {
      return data.id !== '';
  }

  function updateNewPaymentMethodPriority(result: UpsertPaymentMethodSummaryModel) {
      let priority = PaymentMethodPriority.None;

      if (!result.hasPrimary && !result.hasBackup) {
          priority = PaymentMethodPriority.Primary;
      } else if (!result.hasBackup) {
          priority = PaymentMethodPriority.Backup;
      } else {
          priority = PaymentMethodPriority.None;
      }

      setPaymentMethodData(
          (prevData) =>
              new UpsertPaymentMethodModel({
                  ...prevData,
                  priority: priority,
              }),
      );
  }

  useEffect(() => {
      PaymentMethodService.getPaymentMethodSummary(data.contactId, data.id).subscribe({
          next: (response) => {
              setPaymentMethodSummaryInProgress(false);
              if (response.result) {
                  const result = UpsertPaymentMethodSummaryModel.deserializeAPIModel(response.result);
                  setSummaryData(result);

                  if (!isEditMode()) {
                      updateNewPaymentMethodPriority(result);
                  }
              }
          },
      });

      if (data.id) {
          setLoadPaymentMethodInProgress(true);
          PaymentMethodService.getPaymentMethod(data.id).subscribe(
              (response) => {
                  if (response.result) {
                      setLoadPaymentMethodInProgress(false);
                      setPaymentMethodData(UpsertPaymentMethodModel.deserializeAPIModel(response.result));
                      setEditCardBackUp(UpsertPaymentMethodModel.deserializeAPIModel(response.result));
                  }
              },
              (error) => {
                  console.error(error);
              },
          );
      }
      loadContacts();
      loadMerchantAccouts();
  }, []);

  useEffect(() => {
      if (data.merchantAccount && !paymentIntent) {
          createPaymentIntent();
      }
  }, [data.merchantAccount]);

  function loadMerchantAccouts() {
      MerchantAccountService.getMerchantAccounts(AuthService.authData.accountId, 1, 10000, '').subscribe(
          (response) => {
              if (response.result) {
                  const activeMerchantAccounts = (
                      response.result?.map((m) => MerchantAccount.deserializeAPIModel(m)) || []
                  ).filter((f) => f.applicationStatus === ApplicationStatus.Active);

                  const defaultMerchantAccount =
                      activeMerchantAccounts?.length > 0 ? activeMerchantAccounts[0] : undefined;
                  setPaymentMethodData(
                      (prevData) =>
                          new UpsertPaymentMethodModel({
                              ...prevData,
                              merchantAccount: !prevData.merchantAccount
                                  ? defaultMerchantAccount
                                  : prevData.merchantAccount,
                          }),
                  );
                  setMerchantAccounts(activeMerchantAccounts);
              }
          },
          (error) => {
              console.error(error);
          },
      );
  }

  function loadContacts() {
      ContactService.getContacts(AuthService.authData.accountId, 1, 10000, '').subscribe({
          next: (response) => {
              const contactId = paymentMethodData?.contactId;
              const contactData = response?.result?.find((x) => x.id == contactId) || null;
              if (contactData)
                  setPaymentMethodData(
                      (prevData) =>
                          new UpsertPaymentMethodModel({
                              ...prevData,
                              contact: ContactReferenceModel.deserializeFormGridContacts(contactData),
                              contactId: contactId,
                              showLoader: false,
                          }),
                  );
          },
      });
  }

  function row(label: string, rightSide: React.ReactNode, className?: string) {
      {
          return (
              <FlexContainer spacing={30} className={className}>
                  <CustomLabel>{label}</CustomLabel>
                  <div>
                      <FlexContainer alignItems="center" spacing={20}>
                          {rightSide}
                      </FlexContainer>
                  </div>
              </FlexContainer>
          );
      }
  }

  const radioButton = (
      id: string,
      value: boolean,
      fieldName: keyof UpsertPaymentMethodModel,
      label: string,
      updateValue: keyof typeof PaymentMethodPriority,
      disabled: boolean,
  ) => {
      return (
          <Input
              value={value}
              id={`rdb_${id}`}
              type="radio"
              label={label}
              disabled={disabled}
              onChange={() => {
                  if (disabled) return;
                  updateState(fieldName, updateValue);
              }}
              className={'chkbox-no-margin'}
          />
      );
  };

  function updateState(
      key: keyof UpsertPaymentMethodModel,
      value: string | boolean | number | Date | MerchantAccount,
  ) {
      const clonedData = new UpsertPaymentMethodModel(data);
      switch (key) {
          case 'merchantAccount':
              clonedData.merchantAccount = value as MerchantAccount;
              createPaymentIntent();
              break;
          case 'paymentMethodId': 
              clonedData.paymentMethodId = value as string;
              break;
          case 'priority': 
              clonedData.priority = value as PaymentMethodPriority;
              break;
      }
      clonedData.validate(true);
      setPaymentMethodData(clonedData);
  }

  function isEditMode() {
      return data.id !== '';
  }

  function onSubmit() {
      const success = data.validate();
      if (!success) {
          setPaymentMethodData(new UpsertPaymentMethodModel(data));
          return;
      }
      setAPIRequestPending(true);
      const errorMessage = data.id ? 'Error updating payment method' : 'Error saving payment method';
      const successResposne = (
          response: IAPIResponse<IAPICreatePaymentMethodModel> | IAPIResponse<IAPICommonResponse>,
      ) => {
          setAPIRequestPending(false);
          if (response.result) {
              const succesMessage = data.id ? 'Priority updated successfully' : 'Payment method saved successfully';
              ToastService.showSuccess(succesMessage);
              onClose(true);
          } else {
              setAPIRequestPending(false);
              ToastService.showError(errorMessage);
          }
      };
      const errorResponse = (error: any) => {
          console.error(error);
          setAPIRequestPending(false);
          ToastService.showError(errorMessage);
      };
      if (data.id) {
          const udpateRequest = data.createUpdateModel();
          PaymentMethodService.updatePriority(udpateRequest).subscribe({
              next: successResposne,
              error: errorResponse,
          });
      } else {
          const request = data.createNewPaymentMethodAPIModel();
          PaymentMethodService.createPaymentMethod(request).subscribe({
              next: successResposne,
              error: errorResponse,
          });
      }
  }

  function isDiablePriority(priority: keyof typeof PaymentMethodPriority) {
      let disable = false;
      switch (priority) {
          case PaymentMethodPriority.Primary:
              // if (isEditMode()) {
              //     disable = summaryData.paymentMethodsCount == 1 && data.priority == PaymentMethodPriority.Primary;
              // } else {
              //     disable = summaryData.paymentMethodsCount == 0 && data.priority == PaymentMethodPriority.Primary;
              // }

              break;
          case PaymentMethodPriority.Backup:
              if (isEditMode()) {
                  disable = summaryData.paymentMethodsCount == 1;
              } else {
                  disable = summaryData.paymentMethodsCount === 0;
              }

              break;
          case PaymentMethodPriority.None:
              if (isEditMode()) {
                  disable = summaryData.paymentMethodsCount === 1;

                  if (!disable) {
                      disable =
                          summaryData.backUpMethodsCount == 0 ||
                          //if primary then do not allow none
                          editCardBackUp.priority === PaymentMethodPriority.Primary;
                  }
              } else {
                  disable =
                      (summaryData.paymentMethodsCount === 0 && data.priority === PaymentMethodPriority.Primary) ||
                      summaryData.backUpMethodsCount == 0;
              }

              break;
      }
      return disable;
  }

  const bodyContent = () => {
      const priorityContent = () => {
          const primaryDisable = isDiablePriority(PaymentMethodPriority.Primary);
          //data.priority != PaymentMethodPriority.Primary && summaryData.hasPrimary;

          const backupDisable = isDiablePriority(PaymentMethodPriority.Backup);
          // data.priority != PaymentMethodPriority.Backup &&
          // (data.priority === PaymentMethodPriority.Primary || summaryData.hasBackup);
          const noneDisable = isDiablePriority(PaymentMethodPriority.None);
          //data.priority != PaymentMethodPriority.None && data.priority === PaymentMethodPriority.Primary;
          return (
              <FlexContainer spacing={20}>
                  {radioButton(
                      'primary',
                      data.priority === PaymentMethodPriority.Primary,
                      'priority',
                      'Primary',
                      PaymentMethodPriority.Primary,
                      primaryDisable,
                  )}
                  {radioButton(
                      'backup',
                      data.priority === PaymentMethodPriority.Backup,
                      'priority',
                      'Backup',
                      PaymentMethodPriority.Backup,
                      backupDisable,
                  )}
                  {radioButton(
                      'none',
                      data.priority === PaymentMethodPriority.None,
                      'priority',
                      'None',
                      PaymentMethodPriority.None,
                      noneDisable,
                  )}
              </FlexContainer>
          );
      };
      return (
          <PaymentMethodPopupWrapper className="subscription-popup">
              {data.contact &&
                  row('Customer', <RenderContactInfo contact={data.contact} />, 'invoice-description-block')}
              {row(
                  'Merchant Account',
                  isUpdate() ? (
                      <FlexContainer fxLayout="column" spacing={5}>
                          <CustomLabel> {data.merchantAccount?.name}</CustomLabel>
                      </FlexContainer>
                  ) : (
                      <MerchantAccountsDropDown
                          selectedMerchantAccount={data.merchantAccount}
                          merchantAccounts={merchantAccounts}
                          showError={data.hasError('merchantAccount')}
                          onChange={(option) => updateState('merchantAccount', option)}
                          className="default-padding-search-dpdn white-bg"
                      />
                  ),
                  'merchant-account-lh',
              )}
              {row(
                  'Payment Method',
                  isUpdate() ? (
                      <PaymentMethodDescripton paymentMethod={data} />
                  ) : (
                      paymentIntent && (
                          <FlexContainer fxLayout="column">
                              <PaymentForm
                                  paymentIntent={paymentIntent}
                                  onPaymentSuccess={(token: string) => { 
                                      updateState('paymentMethodId', token);
                                  }}
                                  onPaymentError={() => {}}
                                  contact={data.contact || null}
                              />
                              {data.hasError('paymentMethodId') && (
                                  <CustomLabel>
                                      <span className="red-text semi-bold">Please enter valid card details</span>
                                  </CustomLabel>
                              )}
                          </FlexContainer>
                      )
                  ),
                  'merchant-account-lh',
              )}
              <FlexContainer spacing={30}>
                  <CustomLabel>Priority</CustomLabel>
                  {priorityContent()}
              </FlexContainer>
              <CustomLabel
                  fontSize={'14px'}
                  color=" #ff0000"
                  textAlign="center"
                  className="full-width"
                  margin=" 10px 0px 0px"
              >
                  Active Subscriptions and scheduled Invoices will use Primary Payment Method.
                  <br /> Backup will be attempted if Primary fails.
              </CustomLabel>
          </PaymentMethodPopupWrapper>
      );
  };

  const showLoader =
      data.showLoader || summaryData.showLoader || paymentMethodSummaryInProgress || loadPaymentMethodInProgress;
  return (
      <ModalComponent
          title={isUpdate() ? 'Update Priority' : 'New Payment Method'}
          titleFontSize="22px"
          bodyPadding="40px 50px"
          onClose={() => onClose && onClose(false)}
          showFooter={true}
          wrapperWidth="900px"
          bodyBackgroundColor="#fff"
          footerBackground="#F4F7FB"
          footerHeight="auto"
          footerContent={
              <FlexContainer alignItems="center" justifyContent="center" spacing={15}>
                  <SapphireBlueButton minWidth="120px" height="30px" onClick={() => onClose(false)}>
                      Cancel
                  </SapphireBlueButton>
                  <SapphireBlueButton
                      disabled={isAPIRequestPending}
                      primary
                      minWidth="120px"
                      height="30px"
                      onClick={() => onSubmit()}
                  >
                      {isUpdate() ? 'Update' : 'Save'}
                  </SapphireBlueButton>
              </FlexContainer>
          }
      >
          {showLoader ? new Loader(true).loaderComponent : bodyContent()}
      </ModalComponent>
  );
};

export default withRouter(observer(UpsertPaymentMethodPopup));
